import Enumerable from 'linq'
import React, { useEffect, useState } from 'react'
import { SearchOutlined } from '@ant-design/icons'
import { Card, Col, Form, Input, Modal, Radio, Row, Segmented, Space } from 'antd'
import { SearchParameterList } from './SearchParameterList'
import { SearchAndFind, SearchContainerProps, SearchMode } from './type'
import { applySearchParameters, getSearchOptionDefaultValue } from './searchParameters'
import { translate } from '../../translate'
import { ErrorMessage } from '../ErrorMessage'
import { Disable } from '../Disable'
import { NOT_FOUND_MESSAGE } from '../../../App.constants'
import { SortList } from './SortList'
import { SortableRequest } from '../../type'

export const SearchModal = <TRequest extends object>(props: SearchContainerProps<TRequest>) => {
  const {
    open,
    closeMe,
    searchOptions,
    title,
    idLabel,
    findPage,
    findPageLoading,
    onComplete,
    sort,
    onChange,
    searchTarget,
    defaultId,
  } = props
  const [mode, setMode] = useState<SearchMode>(
    searchOptions.some(so => so.defaultActive) ? SearchMode.Search : SearchMode.Find
  )
  const [errors, setErrors] = useState<string[]>([])
  const [activeTarget, setActiveTarget] = useState<string>(searchTarget?.defaultKey ?? '')
  const [form] = Form.useForm<SearchAndFind>()

  useEffect(() => {
    form.setFieldsValue({
      id: defaultId,
      parameters: searchOptions.map(so => ({
        key: so.key,
        isActive: so.defaultActive ?? false,
        exactMatch: false,
        value: getSearchOptionDefaultValue(so),
      })),
      sorts: sort?.defaults ?? []
    })
  }, [])

  const handleConfirm = async () => {
    try {
      await form.validateFields({validateOnly: true, recursive: true})
      setErrors([])
      form.submit()
    } catch (e: any) {
      const errors = Enumerable.from(e.errorFields)
        .selectMany((e: any) => e.errors)
        .toArray() as string[]
      setErrors(errors)
    }
  }

  const handleOnFinish = async (data: SearchAndFind) => {
    if (mode === SearchMode.Find) {
      const {page, error} = await findPage(data.id)

      if (error) {
        const [{message}] = error
        if (message === NOT_FOUND_MESSAGE)
          setErrors([translate(idLabel, 'not_found')])

        return
      }

      return onComplete(null, page, mode)
    }

    const request = applySearchParameters(data.parameters, searchOptions, {} as TRequest)

    if (sort) {
      (request as SortableRequest).sort = data.sorts?.map(st => ({field: st.field, sortOrder: st.direction}))
    }

    if (searchTarget) {
      searchTarget.onApply(activeTarget, request)
    }

    onComplete(request, 1, mode)
  }

  return (
    <Modal
      maskClosable={false}
      title={<Space>
        <SearchOutlined style={{fontSize: 20}}/>
        {title}
      </Space>}
      closable={false}
      open={open}
      onOk={handleConfirm}
      onCancel={closeMe}
      confirmLoading={findPageLoading}
      width='40%'
    >
      <ErrorMessage
        title={translate(title, 'message')}
        message={errors}
        onClose={() => setErrors([])}
      />

      <Form
        name='form'
        form={form}
        onFinish={handleOnFinish}
        onKeyUp={e => e.key === 'Enter' && handleConfirm()}
        onFieldsChange={(_, allFields: any) => {
          onChange?.(allFields)
        }}
      >
        <div className='mt-3 d-block w-100'>
          <Row>
            <Col span={2} className='d-flex flex-column justify-content-center'>
              <Radio checked={mode === SearchMode.Find}
                     onClick={() => setMode(SearchMode.Find)}
              >
                {translate('')}
              </Radio>
            </Col>
            <Col span={22}>
              <Disable disable={mode !== SearchMode.Find}>
                <Card className='mt-1' bordered={false}>
                  <Row>
                    <Col span={8}>
                      <label>{idLabel}</label>
                    </Col>
                    <Col span={10}>
                      <Form.Item
                        noStyle
                        name='id'
                        rules={[
                          {required: mode === SearchMode.Find, message: translate(idLabel, 'is_required')}
                        ]}
                      >
                        <Input/>
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Disable>
            </Col>
          </Row>

          <Row>
            <Col span={2} className='d-flex flex-column justify-content-center'>
              <Radio checked={mode === SearchMode.Search}
                     onClick={() => setMode(SearchMode.Search)}
              >
                {translate('')}
              </Radio>
            </Col>

            <Col span={22}>
              <Disable disable={mode !== SearchMode.Search}>
                <Card bordered={false}>
                  {searchTarget && (
                    <Row gutter={[16, 16]} className='mt-1 mb-2'>
                      <Col span={8}>
                        <label>{translate('search_in')}</label>
                      </Col>
                      <Col span={16}>
                        <Segmented
                          value={activeTarget}
                          onChange={val => setActiveTarget(val as string)}
                          options={searchTarget.options.map(opt => ({
                            label: opt.label,
                            value: opt.key,
                          }))}
                          disabled={mode !== SearchMode.Search}
                        />
                      </Col>
                    </Row>
                  )}
                  <SearchParameterList
                    mode={mode}
                    form={form}
                    options={searchOptions}
                  />
                </Card>
              </Disable>
            </Col>
          </Row>

          {
            sort &&
              <Row className='mt-3'>
                  <Col span={2}></Col>
                  <Col span={22}>
                      <Disable disable={mode === SearchMode.Find}>
                          <Card bordered={false}>
                              <SortList fieldList={sort.fields}/>
                          </Card>
                      </Disable>
                  </Col>
              </Row>
          }
        </div>
      </Form>
    </Modal>
  )
}
